
import { Component, Vue, Prop } from 'vue-property-decorator'
import {
  PROP_UNIT_ID,
  PROP_AMOUNT_ID,
  TERMS_PARAM,
  PROP_MIN_AMOUNT_ID,
  PROP_BE_VARIANT_TABLE_TITLE } from '../helpers/constants'
import { isPropAmountOrUnit, findPropWithId, queryParamUrl, isSalesMultipleRequired } from '../helpers/functions'

@Component({
  template: `
    <div class="mb-2">
      <div class="product__filter-list" v-if='filteredCategories.length > 0 && showFilter'>
        <div>{{ showFilterLabel }}:</div>
        <div class="product__filter-item" v-for="item in filteredCategories">
          {{ getFilteredCategoryDisplayName(item.split(':')[0]) }}
          <i class='icons8-delete' @click='removeFilter(item)'></i>
        </div>
      </div>
      <div
        class="full-width relative-go-to-mobile mb-05"
        :class='{"mb-2": initalShowAll || variants.length < showNumberOfRows}'
      >
        <div class="variants-overflow-container full-width is-scrollable">
          <div class="go-to-container" @click="scrollHorizontal">
            <div class="flex flex-align-center flex-justify-center">
              <i v-if="showRightScrollArrow" class="icons8-chevron-2"></i>
            </div>
          </div>

          <div v-if="showLeftScrollArrow" class="go-left-container" @click="scrollLeftHorizontal">
            <div class="flex flex-align-center flex-justify-center">
              <i class="icons8-chevron-2"></i>
            </div>
          </div>

          <table ref="variantTable" class="variants" :class="{all: showAll}">
            <thead>
              <tr class="variant-headers">
                <th v-if='showDefaultHeaders'>{{ articleNumberTitle }}</th>
                <th v-if='showDefaultHeaders'>{{ articleNameTitle }}</th>
                <th v-for="(header, idx) in visibleHeaders" :class='{"last-in-loop": idx === (visibleHeaders.length - 1)}'>
                  {{ header[1] }}
                </th>
                <th class="amount phone-white">{{ amountHeader }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(variant, index) in variants" :variant="variant" :key="variant.sku" class="variant" :class="getCssClassForVariantId(variant.sku)">
                <td v-if='showDefaultHeaders' class="cp" @click="goToUrl(variant)">
                  <div class="text">{{ variant.sku }}</div>
                </td>
                <td v-if='showDefaultHeaders' class="cp" @click="goToUrl(variant)">
                  <div class="text">{{ getArticleNameTitle(variant.properties, variant.title) }}</div>
                </td>

                <td
                  v-for="(header, idx) in visibleHeaders"
                  @click="goToUrl(variant)"
                  class="cp"
                  :class='{"last-in-loop": idx === (visibleHeaders.length - 1)}'
                >
                  <div class="text" v-html="getPropertyValue(variant, header[0])"></div>
                </td>
                <td class="amount">
                  <add-to-cart-button
                    :product-id="variant.id"
                    :sku="variant.sku"
                    :sales-unit="getSalesUnit(variant.properties)"
                    :multiple-required="getMultipleRequired(variant.properties)"
                    :amount="getAmount(variant.properties)"
                    :label="getSalesLabel(variant.properties)"
                    :placeholder='amountHeader'
                    :measureUnit="getUnit(variant.properties)">
                  </add-to-cart-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        class="full-width flex flex-justify-end gray3--text"
        v-if="!initalShowAll && variants.length > showNumberOfRows"
      >
        <button @click="toggleTableLength()" class=" gray3--text">
          {{ buttonLabel }}
          <i class="icons8-chevron-down-2 ml-025" :class='{"rotate-icon": showAll}'></i>
        </button>
      </div>
    </div>
  `
})

export default class VariantTable extends Vue {
  @Prop() headers!: any
  @Prop() variants!: any
  @Prop({ default: 'Toon meer' }) showAllLabel: string
  @Prop({ default: 'Toon minder' }) showLessLabel: string
  @Prop({ default: 'aantal' }) amountHeader: string
  @Prop({ default: false }) initalShowAll: boolean
  @Prop({ default: '' }) selectedVariant: string
  @Prop({ default: '' }) measureUnitBe: string
  @Prop({ default: false }) showFilter: boolean
  @Prop({ default: 'Gefilterd op'}) showFilterLabel: string
  @Prop({ default: '' }) articleNameTitle: string
  @Prop({ default: '' }) articleNumberTitle: string

  showAll = this.initalShowAll
  showNumberOfRows = 5
  visibleHeaders = []

  showLeftScrollArrow = false
  showRightScrollArrow = true

  showDefaultHeaders = false

  get buttonLabel(): string {
    return this.showAll ? this.showLessLabel : this.showAllLabel
  }

  goToUrl(variant: any) {
    if (variant.url) {
      const queryParams = queryParamUrl(window.location.search, TERMS_PARAM)

      if (queryParams && queryParams.length) {
        window.location.href = variant.url + `?${TERMS_PARAM}=` + encodeURIComponent(queryParams)
      } else {
        window.location.href = variant.url
      }
    }
  }

  get filteredCategories(): string[] | undefined {
    const queryParams = queryParamUrl(window.location.search, TERMS_PARAM)

    if (queryParams && queryParams.length ) {
      const urlParams = queryParams.split(';')
      return urlParams
    } else {
      return []
    }
  }

  scrollHorizontal(item: any) {
    if (this.showRightScrollArrow) {
      const parentTable = item.target.offsetParent.nextSibling.parentElement
      parentTable.scrollLeft += 800
      this.showLeftScrollArrow = true
      this.showRightScrollArrow = false
    }
  }

  scrollLeftHorizontal(item: any) {
    const parentTable = item.target.offsetParent.nextSibling.parentElement
    parentTable.scrollLeft -= 800
    this.showLeftScrollArrow = false
    this.showRightScrollArrow = true
  }

  getArticleNameTitle(variantProps: any, title: string): string {
    return findPropWithId(variantProps, PROP_BE_VARIANT_TABLE_TITLE, title)
  }

  getAmount(variantProps: any): string {
    return findPropWithId(variantProps, PROP_MIN_AMOUNT_ID, '1')
  }

  getSalesUnit(variantProps: any): string {
    return findPropWithId(variantProps, PROP_AMOUNT_ID, '1')
  }

  getMultipleRequired(variantProps: any): boolean {
    return isSalesMultipleRequired(variantProps)
  }

  getUnit(variantProps: any): string {
    return findPropWithId(variantProps, PROP_UNIT_ID, '')
  }

  getSalesLabel(variantProps: any): string {
    if (this.measureUnitBe) return this.measureUnitBe
    else return findPropWithId(variantProps, PROP_UNIT_ID, '')
  }

  getCssClassForVariantId(variantId: string): string {
    return this.selectedVariant === variantId ? 'selected' : ''
  }

  getFilteredCategoryDisplayName(filteredCategory: string): string {
    const splittedCategory = filteredCategory.split('_')
    if (splittedCategory.length > 1) splittedCategory.shift()
    return splittedCategory.join('_')
  }

  toggleTableLength() {
    this.showAll = !this.showAll
  }

  isPropAmountOrUnit(id: string) {
    return isPropAmountOrUnit(id)
  }

  removeFilter(item: string) {

    // Remove filter function. Disabled for now, could change in future. (25-04-22, NSL)

    // const removeFilter = new URLSearchParams(item).toString().replace('=', '')
    // const compareUrl = urlParams.replace(removeFilter, '')

    const urlParams = new URLSearchParams(window.location.search).toString()

    const returnUrl = window.location.href.slice(0, window.location.href.lastIndexOf('/'));

    window.location.href = returnUrl + '?' + urlParams
  }

  getPropertyValue(variant: any, propertyId: string) {
    let propertyValue = ''
    variant.properties.find((prop: any) => {
      if (prop.id === propertyId) {
        const unitValue = prop.attributes.filter((item: any) => item.name.indexOf('UNITOFMEASURE') !== -1)[0]?.value || '';
        if (prop.values.length > 1) propertyValue = prop.values.join(', ');
        else if (unitValue) propertyValue = prop.value + ' ' + unitValue;
        else propertyValue = prop.value;

        return true
      }
      return false
    });


    if (propertyValue == 'True' || propertyValue == 'False') return this.$options?.filters?.t(`${propertyValue.toLowerCase()}Label`)
    return propertyValue
  }

  getHeaderName(propertyId: string) {
    let displayName = null
    this.variants.find((variant: any) => {
      return variant.properties.find((prop: any) => {
        if (prop.id === propertyId) {
          displayName = prop.displayName;
          return true
        }
        return false
      })
    })
    return displayName
  }

  detectEndOfScroll(e: any): void {
    const variantTable = this.$refs.variantTable as HTMLElement
    const parentElement = variantTable.parentElement
    if (parentElement) {
      this.showLeftScrollArrow = parentElement.scrollLeft > 0
      this.showRightScrollArrow = parentElement.scrollLeft < (parentElement.scrollWidth - parentElement.offsetWidth - 1)
    }
  }

  mounted() {
    this.visibleHeaders = this.headers.map((header: any) => {
      const headerName = this.getHeaderName(header)
      if (headerName !== null) {
        return [header, headerName]
      }
    }).filter((header: any) => header != null)

    this.showDefaultHeaders = this.visibleHeaders.length > 0 ? false : true

    const scroll = this.$refs.variantTable as HTMLElement

    this.$nextTick(() => {
      const parentElement = scroll.parentElement

      if (parentElement !== null) {
        this.showRightScrollArrow = parentElement.scrollWidth > parentElement.clientWidth;
      }
    });

    if(scroll) {
      scroll.addEventListener('scroll', this.detectEndOfScroll)
      scroll.addEventListener('wheel',this.detectEndOfScroll)

      //scroll and wheel not working for phone, so need to listen touchmove for phone
      scroll.addEventListener('touchmove', this.detectEndOfScroll)
    }
  }

  destroyed() {
    const scroll = this.$refs.variantTable as HTMLElement

    if(scroll) {
      scroll.removeEventListener('scroll', this.detectEndOfScroll)
      scroll.removeEventListener('wheel',this.detectEndOfScroll)
      scroll.removeEventListener('touchmove',this.detectEndOfScroll)
    }
  }
}
